import {ArrowLeft} from 'lucide-react';
import type {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useLocation} from 'react-router-dom';

import logo from '@/assets/nursio.svg';
import {Button} from '@/components/ui/button';
import {useGroupAuthorize} from '@/hooks/useAuth';
import {UserGroup} from '@/services/graphqlHooks';
import WorkTimeTrackingBanner from '@/shared/work-time/components/WorkTimeTrackingBanner';
import {cn} from '@/utils/ui';

import FloatingMenu from '../floating-menu/FloatingMenu';
import Copyright from '../footer/Copyright';
import HelpDropdown from '../HelpDropdown';
import IdleTimer from '../IdleTimer';
import MyAccountDropdown from '../MyAccountDropdown';
import MyWorkDropdown from '../MyWorkDropdown';

export type SimpleLayoutProps = {
  children: ReactNode;
  className?: string;
};

const SimpleLayout = ({children, className}: SimpleLayoutProps) => {
  const {t} = useTranslation();
  const {pathname} = useLocation();
  const isRelative = useGroupAuthorize([UserGroup.AuthorizedRelatives]);
  const showBackToHome = !isRelative && pathname !== '/today';

  return (
    <div className="flex min-h-full sm:flex-row">
      <div className="flex grow flex-col overflow-hidden max-sm:pb-28">
        <header className="flex flex-0 flex-row items-center gap-2 p-5">
          <Link to="/">
            <img src={logo} alt="Nursiolink" />
          </Link>
          <div className="grow"></div>
          <IdleTimer />
          <MyWorkDropdown />
          <MyAccountDropdown />
          <HelpDropdown />
        </header>
        <WorkTimeTrackingBanner />
        <main className="flex-1">
          <div className={cn('p-5', className)}>
            {showBackToHome && (
              <Button variant="tertiary" className="mb-4" asChild>
                <Link to="/">
                  <ArrowLeft />
                  {t('Back to homepage')}
                </Link>
              </Button>
            )}
            {children}
          </div>
        </main>
        <footer className="flex-0">
          <Copyright />
        </footer>
      </div>
      {!isRelative && <FloatingMenu />}
    </div>
  );
};

export default SimpleLayout;
